import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PartyPopper, Award, Users, DollarSign } from "lucide-react";
import { useReportingData } from "@/hooks/useReportingData";
import Confetti from "react-confetti";

const MILESTONES = [
  { type: "revenue", value: 100000, label: "$100,000 Revenue", icon: DollarSign },
  { type: "revenue", value: 500000, label: "$500,000 Revenue", icon: DollarSign },
  { type: "students", value: 500, label: "500 Students Enrolled", icon: Users },
  { type: "students", value: 1000, label: "1,000 Students Enrolled", icon: Users },
];

export default function MilestoneCelebration() {
  const { revenueData, enrollmentData } = useReportingData();
  const [milestone, setMilestone] = useState<null | typeof MILESTONES[0]>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Calculate total revenue and total students
    const totalRevenue = revenueData.reduce((sum, d) => sum + (d.revenue || 0), 0);
    const totalStudents = enrollmentData.reduce((sum, d) => sum + (d.newStudents || 0), 0);

    // Find the highest milestone reached
    let reached: null | typeof MILESTONES[0] = null;
    for (const m of MILESTONES) {
      if (m.type === "revenue" && totalRevenue >= m.value) reached = m;
      if (m.type === "students" && totalStudents >= m.value) reached = m;
    }
    setMilestone(reached);
    setShowConfetti(!!reached);
  }, [revenueData, enrollmentData]);

  if (!milestone) return null;

  const Icon = milestone.icon;

  return (
    <div className="relative">
      {showConfetti && <Confetti numberOfPieces={300} recycle={false} className="pointer-events-none" />}
      <Card className="border-l-8 border-emerald-500 shadow-lg bg-gradient-to-br from-emerald-50 via-white to-emerald-100/80 animate-fade-in">
        <CardHeader className="flex flex-row items-center gap-4 pb-2">
          <PartyPopper className="h-10 w-10 text-emerald-500 animate-bounce" />
          <CardTitle className="text-2xl font-bold text-emerald-700 flex items-center gap-2">
            <Icon className="h-7 w-7 text-emerald-400" />
            {milestone.label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-lg text-emerald-800 font-semibold mb-2">Congratulations! 🎉</p>
          <p className="text-muted-foreground">Your organization just reached a major milestone. Keep up the amazing work!</p>
        </CardContent>
      </Card>
    </div>
  );
} 